import type { Agent } from '@studio/models/agent';
import type { Room } from '@studio/models/room';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { Preview } from './types.js';
import * as Commands from '#~/device/commands/index.js';
import type { Discovery$Audio } from '#~/device/discovery/index.js';
import type { Device$Audio, Minimist } from '#~/device/index.js';
import { toDevice } from '#~/device/index.js';
import { logger } from '#~/logger/index.js';
import { extractMachineIDAndTypeFromPersistentID } from '#~/mediadevices/discovery.js';

/**
 * Wrapper around the standard `create` device command that sets the appropriate device defaults.
 *
 * @param roomId - Room ID.
 * @param agentId - Agent ID.
 * @param candidate - Candidate device.
 * @param token - The token.
 */
export const addDevice = async (
  roomId: Room['id'],
  agentId: Agent['id'],
  candidate: Minimist,
  token: string
): Promise<void> => {
  await Commands.add(roomId, agentId, toDevice(candidate), token);
};

/** @internal */

export const isDiscoveryInUse = (device: Device$Audio, discovery: Discovery$Audio): boolean => {
  // dequal(device.audio.config, discovery.audio);
  const a = device.audio.config;
  const b = discovery.audio;

  const hostMatches = 'host' in a && 'host' in b && a.host === b.host;
  // const portMatches = 'port' in a && 'port' in b && a.port === b.port;

  // const isNDI = a.protocol === 'ndi' && b.protocol === 'ndi';
  // const isRTSP = a.protocol.startsWith('rtsp') && (b.protocol === a.protocol || b.protocol === 'onvif');

  // if (isNDI) {
  //   return hostMatches && portMatches;
  // }

  // if (isRTSP) {
  //   return hostMatches;
  // }

  if (
    'path' in a &&
    'path' in b &&
    'protocol' in a &&
    'protocol' in b &&
    a.protocol === b.protocol &&
    a.protocol === 'browsermedia'
  ) {
    const {
      machinePersistentID: machinePersistentIDA,
      label: labelA,
      offset: offsetA
    } = extractMachineIDAndTypeFromPersistentID(a.path as string);
    const {
      machinePersistentID: machinePersistentIDB,
      label: labelB,
      offset: offsetB
    } = extractMachineIDAndTypeFromPersistentID(b.path as string);
    const pathMatches =
      !!machinePersistentIDA &&
      machinePersistentIDA === machinePersistentIDB &&
      labelA === labelB &&
      offsetA === offsetB;

    return hostMatches || pathMatches;
  }

  const protocolMatches = a.protocol === b.protocol;
  return hostMatches && protocolMatches;
};

/** @internal */
export const removePreview = async (preview: T_Maybe<Preview>, token: string): Promise<void> => {
  const deviceId = preview?.deviceId;

  if (!deviceId) {
    return undefined;
  }

  logger.debug(`Removing preview device: ${deviceId}`);

  return Commands.remove(deviceId, token);
};
